/**
 * The table every /overview list uses (boxes 9, 11, 12, 13): numeric columns
 * right-aligned, a small-sample row faded (R17, from raw impressions), and a
 * horizontal scroll inside the box so the page never scrolls sideways. Its
 * cell pieces too: the chips, and the Result and Cost per Result figures with
 * the metric on a second line (rules-04 E3). In print a cell wraps, so that
 * line adds height, not width.
 *
 * @module components/overview/OverviewTable
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { costBasisText, DASH, fmtMoney, fmtResult, resultMetricName } from '@/services/overview/overviewFormat';
import type { ResultState } from '@/services/overview/overviewTypes';
import type { ResultMetric } from '@/services/targets/resultMetric';

export interface OverviewColumn<R> {
  id: string;
  header: React.ReactNode;
  numeric?: boolean;
  cell: (r: R) => React.ReactNode;
}

interface Props<R> {
  columns: ReadonlyArray<OverviewColumn<R>>;
  rows: readonly R[];
  rowKey: (r: R) => string;
  faded?: (r: R) => boolean;
}

/** Amber chip beside a row's first cell: "Unclassified", "google ไม่ส่ง Clicks" (the /looker chip style). */
export const Chip: React.FC<{ text: string }> = ({ text }) => (
  <span className="ml-2 inline-block whitespace-nowrap rounded-full border border-warning-700 px-1.5 text-[11px] text-warning-700 print:whitespace-normal">{text}</span>
);

/** DEC3 §4.3's badge "นิยามของ workspace: Reach" after the objective, where resultMetric's reachBadgeShown says so. Nothing when off. */
export const ReachBadge: React.FC<{ on: boolean }> = ({ on }) => {
  const { t } = useTranslation();
  return on
    ? <span className="ml-2 inline-block whitespace-nowrap rounded-full border border-ink-900 px-1.5 text-[11px] text-ink-900">{t('looker.resultMapping.badge')}</span>
    : null;
};

/** The "n<500" marker beside a faded row's first cell (R17); nothing when off. */
export const LowSample: React.FC<{ on: boolean }> = ({ on }) => {
  const { t } = useTranslation();
  return on ? <span className="ml-2 text-xs not-italic text-neutral-700">{t('overview.lowSample')}</span> : null;
};

/** The second line under a figure, as D3's ResultCell draws it: small, and upright in a faded row. */
const FIGURE_LINE_CLASS = 'block text-xs not-italic text-neutral-700';

/**
 * Boxes 9 and 13: a Result, then on a small second line the metric it counts, as D3 prints it (rules-04 E3): one
 * header cannot name three metrics. "ไม่ได้รายงาน" names the metric it lacks; nothing under "—".
 */
export const ResultFigure: React.FC<{ row: { metric: ResultMetric | null; result: number | null; resultState: ResultState } }> = ({ row }) => {
  const { t } = useTranslation();
  return (
    <>
      <span className="block">{fmtResult(row, t)}</span>
      {row.metric !== null && row.resultState !== 'none' && (
        <span data-result-metric className={FIGURE_LINE_CLASS}>{resultMetricName(row.metric, t)}</span>
      )}
    </>
  );
};

/** Box 9: a Cost per Result, then on a second line what it is per ("ต้นทุน / 1,000 impressions"); nothing under "—". */
export const CostFigure: React.FC<{ row: { metric: ResultMetric | null; costPerResult: number | null }; currency: string | null }> = ({ row, currency }) => {
  const { t } = useTranslation();
  const value = fmtMoney(row.costPerResult, currency);
  return (
    <>
      <span className="block">{value}</span>
      {row.metric !== null && value !== DASH && <span data-cost-metric className={FIGURE_LINE_CLASS}>{costBasisText(row.metric, t)}</span>}
    </>
  );
};

export const OverviewTable = <R,>({ columns, rows, rowKey, faded }: Props<R>): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <div className="max-w-full overflow-x-auto rounded-xl border border-neutral-200 print:overflow-visible">
      <table className="min-w-full text-sm print:w-full">
        <thead className="bg-neutral-50">
          <tr>
            {columns.map((c) => (
              <th key={c.id} scope="col" className={`whitespace-nowrap px-3 py-2 text-xs font-semibold text-neutral-800 ${c.numeric ? 'text-right' : 'text-left'}`}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const low = faded ? faded(r) : false;
            return (
              <tr key={rowKey(r)} data-low-sample={low ? 'true' : undefined} title={low ? t('overview.lowSampleTitle') : undefined}
                className={`border-t border-neutral-100 ${low ? 'italic text-neutral-700' : 'text-neutral-900'}`}>
                {columns.map((c) => (
                  <td key={c.id} className={`whitespace-nowrap px-3 py-2 print:whitespace-normal ${c.numeric ? 'text-right tabular-nums' : ''}`}>{c.cell(r)}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
